import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import { formatMoney } from '../../utils/formatMoney';
import { formatCostSource, costSourceTooltip } from '../../utils/costSource';
import { parseApiDate } from '../../utils/parseApiDate';

function formatSpeed(row) {
  const ms = row.response_time_ms;
  const tokens = row.total_tokens;
  if (!ms || ms <= 0 || !tokens) return '—';
  return `${(tokens / (ms / 1000)).toFixed(1)} tok/s`;
}

function formatDate(iso) {
  const date = parseApiDate(iso);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(',', '');
}

function AssistantCell({ row }) {
  const navigate = useNavigate();
  const name = row.assistant_name || '—';
  const model = row.model;

  return (
    <div className="usage-log-assistant-cell">
      {row.assistant_id ? (
        <button
          type="button"
          className="usage-log-assistant-link"
          onClick={() => navigate(`/assistants/${row.assistant_id}/edit`)}
        >
          {name}
        </button>
      ) : (
        <span className="usage-log-assistant-name">{name}</span>
      )}
      {model ? <span className="usage-log-assistant-model">{model}</span> : null}
    </div>
  );
}

export default function UsageLogTable({
  data,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
}) {
  const columns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 118,
      render: (v) => <span className="usage-log-nowrap">{formatDate(v)}</span>,
    },
    {
      title: 'Assistant',
      key: 'assistant',
      width: 220,
      ellipsis: true,
      render: (_, row) => <AssistantCell row={row} />,
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 88,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 80,
      render: (v) => <span className="usage-log-nowrap">{v === 'api' ? 'API' : v}</span>,
    },
    {
      title: 'Input',
      dataIndex: 'input_tokens',
      key: 'input_tokens',
      width: 72,
      align: 'right',
      render: (v) => (v != null ? <span className="usage-log-nowrap">{v.toLocaleString()}</span> : '—'),
    },
    {
      title: 'Output',
      dataIndex: 'output_tokens',
      key: 'output_tokens',
      width: 80,
      align: 'right',
      render: (v) => (v != null ? <span className="usage-log-nowrap">{v.toLocaleString()}</span> : '—'),
    },
    {
      title: 'Cost',
      dataIndex: 'cost_usd',
      key: 'cost_usd',
      width: 88,
      align: 'right',
      render: (v) => <span className="usage-log-nowrap">{formatMoney(v)}</span>,
    },
    {
      title: 'Source',
      dataIndex: 'cost_source',
      key: 'cost_source',
      width: 100,
      ellipsis: true,
      render: (v) => {
        const label = formatCostSource(v);
        const tip = costSourceTooltip(v);
        if (!label) return '—';
        return tip ? <Tooltip title={tip}>{label}</Tooltip> : label;
      },
    },
    {
      title: 'Speed',
      key: 'speed',
      width: 88,
      align: 'right',
      render: (_, row) => <span className="usage-log-nowrap">{formatSpeed(row)}</span>,
    },
    {
      title: 'TTFT',
      dataIndex: 'time_to_first_token_ms',
      key: 'ttft',
      width: 64,
      align: 'right',
      render: (v) => (
        <span className="usage-log-nowrap">
          {v != null ? `${(v / 1000).toFixed(2)} s` : '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 72,
      ellipsis: true,
      render: (_, row) => {
        if (row.status_code >= 400) return row.error_type || `HTTP ${row.status_code}`;
        return 'OK';
      },
    },
  ], []);

  return (
    <Table
      className="usage-log-table"
      rowKey="id"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={data}
      tableLayout="fixed"
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: false,
        onChange: onPageChange,
      }}
      scroll={{ x: 'max-content' }}
    />
  );
}
